import { createClient } from '@supabase/supabase-js'
import { CLE_PUBLISHABLE, URL_SUPABASE } from './supabase'

/**
 * « Mot de passe oublié ? » de l'écran de connexion de CETTE console.
 *
 * La page qui reçoit le lien de l'e-mail est UNE seule pour toutes les surfaces
 * SwiftDeliver : `https://deliver.yiriwa-group.com/reinitialiser` (voir
 * `webapp-ts/src/ui/Reinitialiser.tsx`). Le paramètre `retour` lui dit où renvoyer
 * la personne une fois le mot de passe changé. Il ne prend que des valeurs d'une
 * LISTE FERMÉE (jamais une URL libre : pas de redirection ouverte).
 *
 * Même module, copié dans `admin-ts` et `webapp-ts` : chaque console se construit
 * seule, sans code partagé.
 */

/** Valeurs acceptées pour `?retour=` — la même liste dans les trois consoles. */
export const RETOURS = ['ops', 'admin', 'deliver'] as const
export type Retour = (typeof RETOURS)[number]

/** Cette console : celle vers laquelle le lien ramènera. */
export const RETOUR_CONSOLE: Retour = 'admin'

/**
 * URL EXACTE de la page de réinitialisation, à déclarer dans « Redirect URLs » de
 * Supabase Auth (le motif `?retour=*` doit y être admis). Constante, pas
 * `window.location.origin` : cette console vit sur un autre sous-domaine.
 */
export const URL_REINITIALISATION = 'https://deliver.yiriwa-group.com/reinitialiser'

/** Valeur de `?retour=` reconnue, sinon `null`. Liste fermée : tout le reste est ignoré. */
export function lireRetour(valeur: string | null | undefined): Retour | null {
  return RETOURS.find((r) => r === valeur) ?? null
}

/** Adresse que Supabase met dans le lien de l'e-mail. */
export function urlRedirection(retour: Retour): string {
  return `${URL_REINITIALISATION}?retour=${retour}`
}

/**
 * Réponse NEUTRE, la même que l'adresse existe ou non : dire « aucun compte avec
 * cette adresse » permettrait de sonder qui a un compte.
 */
export const MESSAGE_LIEN_ENVOYE =
  'Si un compte existe pour cette adresse, un e-mail vient d’être envoyé. Ouvrez le lien pour choisir un nouveau mot de passe.'

export const MESSAGE_TROP_DE_DEMANDES = 'Trop de demandes, réessayez dans quelques minutes.'
export const MESSAGE_EMAIL_INVALIDE = 'Saisissez une adresse e-mail valide.'
export const MESSAGE_ENVOI_IMPOSSIBLE = 'Envoi impossible pour le moment. Réessayez dans un instant.'
const MESSAGE_RESEAU = 'Connexion impossible. Vérifiez votre réseau et réessayez.'

export const emailValide = (e: string): boolean => /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(e.trim())

interface ErreurAuth {
  message?: string | undefined
  code?: string | undefined
  status?: number | undefined
}

/** Erreur de Supabase Auth (en anglais) → phrase. Jamais le texte brut. */
export function traduireErreurOubli(err: ErreurAuth | null | undefined): string {
  const m = (err?.message ?? '').toLowerCase()
  const c = (err?.code ?? '').toLowerCase()
  if (err?.status === 429 || c.includes('rate_limit') || m.includes('rate limit') || m.includes('security purposes') || m.includes('too many')) {
    return MESSAGE_TROP_DE_DEMANDES
  }
  if (m.includes('failed to fetch') || m.includes('network') || m.includes('load failed')) return MESSAGE_RESEAU
  return MESSAGE_ENVOI_IMPOSSIBLE
}

/** Ce que `demanderReinitialisation` utilise du client Supabase (injectable en test). */
export interface ClientDemande {
  auth: {
    resetPasswordForEmail: (email: string, options: { redirectTo: string }) => Promise<{ error: ErreurAuth | null }>
  }
}

/**
 * Un client SANS session ni stockage, en flux IMPLICITE.
 *
 * Pourquoi pas le client PKCE de la console : en PKCE, le lien de l'e-mail revient
 * avec un `?code=` qui ne s'échange que dans le NAVIGATEUR ET SUR LE SOUS-DOMAINE
 * qui a fait la demande (le « code verifier » y est rangé). Or la page de
 * réinitialisation est sur un autre sous-domaine. En flux implicite, le lien porte
 * ses jetons dans le fragment (`#access_token=…`) et marche depuis n'importe quel
 * appareil — comme pour l'app Livreur.
 */
const clientIsole = (): ClientDemande =>
  createClient(URL_SUPABASE, CLE_PUBLISHABLE, {
    auth: {
      flowType: 'implicit',
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: 'sb-swiftdeliver-demande-reinit',
    },
  })

/**
 * Demande l'e-mail de réinitialisation. Lève une `Error` à phrase lisible si la
 * saisie est invalide ou l'envoi refusé ; sinon rend sans rien dire sur l'existence
 * du compte (l'appelant affiche `MESSAGE_LIEN_ENVOYE`).
 */
export async function demanderReinitialisation(
  email: string,
  retour: Retour = RETOUR_CONSOLE,
  client: ClientDemande = clientIsole(),
): Promise<void> {
  if (!emailValide(email)) throw new Error(MESSAGE_EMAIL_INVALIDE)
  let erreur: ErreurAuth | null
  try {
    const r = await client.auth.resetPasswordForEmail(email.trim().toLowerCase(), { redirectTo: urlRedirection(lireRetour(retour) ?? RETOUR_CONSOLE) })
    erreur = r.error
  } catch (e) {
    erreur = { message: e instanceof Error ? e.message : '' }
  }
  if (erreur) throw new Error(traduireErreurOubli(erreur))
}
